import type { CalcKey } from '../locales/keys';

export interface SlopeFieldPreset {
  id: string;
  labelKey: CalcKey;
  /**
   * dy/dx as an expression in `x` and `y`.
   * Must be parseable and evaluatable with scope `{ x, y }`.
   */
  g: string;
  /**
   * A closed-form f(x) satisfying y′ = g(x, y) for a particular initial condition,
   * provided only when a clean single-valued expression exists.
   * Omitted when the general solution requires an arbitrary constant C that cannot
   * be absorbed into a single canonical formula (e.g. implicit curves or families).
   */
  solution?: string;
  /** Visible domain [lo, hi] for the x-axis of the plot. */
  domain: [number, number];
}

export const SLOPE_FIELD_PRESETS: SlopeFieldPreset[] = [
  {
    id: 'exponential-growth',
    labelKey: 'preset.slope-field.exponential-growth',
    // y′ = y — solution through (0, 1) is exp(x); the whole field of
    // integral curves is the family y = C·e^x.
    g: 'y',
    solution: 'exp(x)',
    domain: [-3, 3],
  },
  {
    id: 'linear-x',
    labelKey: 'preset.slope-field.linear-x',
    // y′ = x — solution through (0, 0) is x^2 / 2; integral curves
    // are upward-opening parabolas y = x^2 / 2 + C.
    g: 'x',
    solution: 'x^2 / 2',
    domain: [-3, 3],
  },
  {
    id: 'circles',
    labelKey: 'preset.slope-field.circles',
    // y′ = -x/y — integral curves are concentric circles x^2 + y^2 = r^2.
    // There is no single-valued closed-form f(x) for the full family,
    // so `solution` is intentionally omitted.
    g: '-x / y',
    domain: [-3, 3],
  },
  {
    id: 'linear-ode',
    labelKey: 'preset.slope-field.linear-ode',
    // y′ = x - y — the general solution y = x - 1 + C·e^(-x) depends on
    // the arbitrary constant C, so no single canonical closed form is given.
    g: 'x - y',
    domain: [-3, 3],
  },
];
